import { useTranslation } from "react-i18next";

import type { AppItem, AppCategory } from "../AppsSidebar/types";

/**
 * La rejilla de aplicaciones: a dónde puedo ir, de un vistazo.
 *
 * ── Qué se fue, y por qué ───────────────────────────────────────────────────
 * Cada ficha era un botón con fondo, borde y 92 px de alto; cada categoría, un
 * acordeón con dos filetes, un icono, un rótulo en versales y un galón para
 * plegarla. Con nueve aplicaciones eso es más cromo que contenido.
 *
 * El acordeón, además, escondía apps detrás de un clic y guardaba un estado
 * —qué estaba plegado— que había que recordar entre aperturas. Para un catálogo
 * que cabe de una vez, plegar no ahorra nada: cuesta un clic y una duda.
 *
 * Ahora la ficha no tiene caja hasta que la tocas, el rótulo de categoría es
 * una línea de texto, y todo está desplegado. La rejilla se recorre con el ojo,
 * que es más rápido que con el dedo.
 *
 * El icono conserva su color de marca: es lo único que distingue una app de
 * otra a tamaño de miniatura, y apagarlo dejaría nueve cuadros grises iguales.
 */

/**
 * Un bloque de categorías con su propio rótulo (CC-CONSOLE #1634): con caso de
 * uso, el menú va «grupo de la compañía» y después «otras aplicaciones». Sin
 * caso de uso no hay bloques y la rejilla es la de siempre.
 */
export interface GrupoDeApps {
  id: string;
  titulo: string;
  categorias: AppCategory[];
}

interface Props {
  categorias: AppCategory[];
  appSeleccionada?: string;
  onElegir: (app: AppItem, categoriaId: string, categoriaTitulo: string) => void;
  /** Si viene, manda sobre `categorias` y dibuja un rótulo discreto por bloque. */
  grupos?: GrupoDeApps[];
}

export default function RejillaDeApps({
  categorias,
  appSeleccionada,
  onElegir,
  grupos,
}: Props) {
  const { t } = useTranslation("common");

  const vacio = grupos ? grupos.every((g) => g.categorias.length === 0) : categorias.length === 0;
  if (vacio) {
    return (
      <p className="px-4 py-8 text-center text-xs text-content-muted">
        {t("appsSidebar.noApps", "No hay aplicaciones disponibles")}
      </p>
    );
  }

  const renderCategorias = (lista: AppCategory[], Rotulo: "h3" | "h4") =>
    lista.map((categoria, i) => (
      <section key={categoria.id}>
        {/*
          El filete separador va ENTRE categorías, no encima de la primera:
          una línea antes del primer grupo separa el grupo de la nada.
        */}
        {i > 0 && (
          <div className="mb-3 h-px bg-line-soft" aria-hidden="true" />
        )}
        <Rotulo className="hud-rotulo mb-2 px-1">{categoria.title}</Rotulo>
        <ul className="grid grid-cols-3 gap-1">
          {categoria.apps.map((app) => (
            <li key={app.id}>
              <FichaDeApp
                app={app}
                seleccionada={appSeleccionada === app.id}
                onElegir={() => onElegir(app, categoria.id, categoria.title)}
              />
            </li>
          ))}
        </ul>
      </section>
    ));

  if (!grupos) {
    return (
      <div className="flex flex-col gap-4 px-3 py-3">
        {renderCategorias(categorias, "h3")}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 px-3 py-3">
      {grupos
        .filter((g) => g.categorias.length > 0)
        .map((grupo, i) => (
          <section key={grupo.id} aria-labelledby={`apps-grupo-${grupo.id}`} data-testid={`apps-grupo-${grupo.id}`}>
            {/*
              Rótulo de bloque, pequeño y en el color de marca: separa «lo de tu
              caso de uso» de «lo demás» sin competir con los títulos de categoría.
            */}
            <h3
              id={`apps-grupo-${grupo.id}`}
              className={`mb-2 px-1 text-[10px] font-bold uppercase tracking-wide text-primary-500 ${
                i > 0 ? "mt-1 border-t border-line-soft pt-3" : ""
              }`}
            >
              {grupo.titulo}
            </h3>
            <div className="flex flex-col gap-4">{renderCategorias(grupo.categorias, "h4")}</div>
          </section>
        ))}
    </div>
  );
}

function FichaDeApp({
  app,
  seleccionada,
  onElegir,
}: {
  app: AppItem;
  seleccionada: boolean;
  onElegir: () => void;
}) {
  const { t } = useTranslation("common");
  const bloqueada = app.disabled || app.isComingSoon;
  const distintivo = app.isComingSoon
    ? t("appsSidebar.comingSoonBadge")
    : app.isBeta
      ? "Beta"
      : app.badge;

  return (
    <button
      type="button"
      onClick={onElegir}
      disabled={bloqueada}
      // La descripción completa sigue aquí para quien use el cursor; nada
      // depende SÓLO de ella, que es la regla que rompía el diseño anterior.
      title={app.description}
      aria-current={seleccionada ? "page" : undefined}
      /*
        76 px de alto y sin caja. El mínimo táctil se cumple de sobra y, al no
        tener fondo, lo que se ve es el icono y el nombre: el resto era marco.
      */
      className={`group flex min-h-[76px] w-full flex-col items-center justify-center gap-1.5 rounded-lg px-1 py-2 transition-colors ${
        bloqueada
          ? "cursor-not-allowed opacity-45"
          : seleccionada
            ? "bg-subtle"
            : "hover:bg-subtle/70"
      }`}
    >
      <span className="relative flex h-10 w-10 items-center justify-center">
        <span
          className={`flex h-10 w-10 items-center justify-center transition-transform duration-150 ${
            bloqueada ? "grayscale" : "group-hover:scale-105"
          }`}
        >
          {app.icon}
        </span>
        {distintivo && (
          <span className="absolute -right-1.5 -top-1 rounded-sm bg-content px-1 text-[8px] font-bold uppercase leading-[1.4] text-app">
            {distintivo}
          </span>
        )}
      </span>
      {/*
        Dos líneas como máximo. El nombre completo no cabe en 100 px y
        recortarlo a una línea convierte «Administración de Flotilla» y
        «Administración de Usuarios» en el mismo texto.
      */}
      <span
        className={`line-clamp-2 text-center text-[11px] leading-tight ${
          seleccionada ? "font-semibold text-content" : "text-content-secondary"
        }`}
      >
        {app.name}
      </span>
    </button>
  );
}
